import React, { useEffect, useRef } from "react";
import { fmtInt, fmtMoney, fmtPct, fmtPrice, num } from "../../lib/capTableMath";

// Uncontrolled input that commits on the native `change` event (blur /
// Enter / spinner), matching the original calculator's onchange handlers.
export function CommitInput({ value, onCommit, ...rest }) {
  const ref = useRef(null);
  const cb = useRef(onCommit);
  cb.current = onCommit;
  useEffect(() => {
    const el = ref.current;
    const h = () => cb.current(el.value);
    el.addEventListener("change", h);
    return () => el.removeEventListener("change", h);
  }, []);
  return <input ref={ref} defaultValue={value ?? ""} {...rest} />;
}

function Table({ id, heads, children, style }) {
  return (
    <div className="table-wrap">
      <table id={id} style={style}>
        <thead>
          <tr>{heads.map(([label, width, numeric], i) => (
            <th key={i} style={{ width }} className={numeric ? "numeric" : undefined}>{label}</th>
          ))}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const cell = (heads, i) => ({ "data-label": heads[i][0] });

const FOUNDER_HEADS = [["Founder", "30%"], ["Role", "30%"], ["Shares", "20%", 1], ["Ownership at founding", "12%", 1], ["", "8%"]];
export function FoundersTable({ state, tick, update, ro }) {
  const total = state.founders.reduce((s, f) => s + num(f.shares), 0) || 1;
  const H = FOUNDER_HEADS;
  return (
    <>
      <Table id="foundersTable" heads={H}>
        {state.founders.map((f) => (
          <tr key={`${f.id}-${tick}`} data-testid={`founder-row-${f.id}`}>
            <td {...cell(H, 0)}><CommitInput type="text" value={f.name} disabled={ro} placeholder="Full name" onCommit={(v) => update(() => { f.name = v; })} /></td>
            <td {...cell(H, 1)}><CommitInput type="text" value={f.role} disabled={ro} placeholder="e.g. CEO" onCommit={(v) => update(() => { f.role = v; })} /></td>
            <td {...cell(H, 2)} className="numeric"><CommitInput type="number" min="0" value={f.shares} disabled={ro} onCommit={(v) => update(() => { f.shares = num(v); })} /></td>
            <td {...cell(H, 3)} className="numeric">{fmtPct(f.shares / total)}</td>
            <td {...cell(H, 4)} className="numeric"><button className="btn btn-danger" disabled={ro} onClick={() => update((s) => { s.founders = s.founders.filter((x) => x.id !== f.id); })}>Remove</button></td>
          </tr>
        ))}
      </Table>
      {!state.founders.length && <div id="foundersEmpty" className="empty-state">No founders yet — click "Add founder" to start your cap table.</div>}
    </>
  );
}

const SAFE_HEADS = [["Investor", "22%"], ["Amount ($)", "14%", 1], ["Post-money cap ($)", "18%", 1], ["Discount (%)", "12%", 1], ["Est. ownership at cap", "14%", 1], ["Converted?", "12%", 1], ["", "8%"]];
export function SafesTable({ state, tick, update, ro }) {
  const H = SAFE_HEADS;
  return (
    <>
      <Table id="safesTable" heads={H}>
        {state.safes.map((s) => {
          const capPct = s.cap > 0 ? (num(s.amount) / num(s.cap)) : 0;
          return (
            <tr key={`${s.id}-${tick}`} data-testid={`safe-row-${s.id}`}>
              <td {...cell(H, 0)}><CommitInput type="text" value={s.investor} disabled={ro} placeholder="Investor name" onCommit={(v) => update(() => { s.investor = v; })} /></td>
              <td {...cell(H, 1)} className="numeric"><CommitInput type="number" min="0" step="1000" value={s.amount} disabled={ro} onCommit={(v) => update(() => { s.amount = num(v); })} /></td>
              <td {...cell(H, 2)} className="numeric"><CommitInput type="number" min="0" step="100000" value={s.cap} disabled={ro} onCommit={(v) => update(() => { s.cap = num(v); })} /></td>
              <td {...cell(H, 3)} className="numeric"><CommitInput type="number" min="0" max="50" step="1" value={s.discount} disabled={ro} onCommit={(v) => update(() => { s.discount = num(v); })} /></td>
              <td {...cell(H, 4)} className="numeric">{fmtPct(capPct)}</td>
              <td {...cell(H, 5)} className="numeric" style={{ color: s.converted ? "var(--teal)" : "var(--text-muted)" }}>{s.converted ? "Yes" : "Pending"}</td>
              <td {...cell(H, 6)} className="numeric"><button className="btn btn-danger" disabled={ro} onClick={() => update((st) => { st.safes = st.safes.filter((x) => x.id !== s.id); })}>Remove</button></td>
            </tr>
          );
        })}
      </Table>
      {!state.safes.length && <div id="safesEmpty" className="empty-state">No open SAFEs — click "Add SAFE" to add one.</div>}
    </>
  );
}

const ROUND_HEADS = [["Round", "18%"], ["Amount ($)", "14%", 1], ["Pre-money ($)", "18%", 1], ["Price / share", "14%", 1], ["Shares issued", "14%", 1], ["Investor %", "14%", 1], ["", "8%"]];
export function RoundsTable({ state, computed, tick, update, ro }) {
  const H = ROUND_HEADS;
  const results = new Map(computed.roundResults.map((r) => [r.id, r]));
  return (
    <>
      <Table id="roundsTable" heads={H}>
        {state.rounds.map((r) => {
          const res = results.get(r.id) || {};
          return (
            <tr key={`${r.id}-${tick}`} data-testid={`round-row-${r.id}`}>
              <td {...cell(H, 0)}><CommitInput type="text" value={r.name} disabled={ro} placeholder="Round name" onCommit={(v) => update(() => { r.name = v; })} /></td>
              <td {...cell(H, 1)} className="numeric"><CommitInput type="number" min="0" step="10000" value={r.amount} disabled={ro} onCommit={(v) => update(() => { r.amount = num(v); })} /></td>
              <td {...cell(H, 2)} className="numeric"><CommitInput type="number" min="0" step="100000" value={r.preMoney} disabled={ro} onCommit={(v) => update(() => { r.preMoney = num(v); })} /></td>
              <td {...cell(H, 3)} className="numeric">{res.pricePerShare ? fmtPrice(res.pricePerShare) : "—"}</td>
              <td {...cell(H, 4)} className="numeric">{res.sharesIssued ? fmtInt(res.sharesIssued) : "—"}</td>
              <td {...cell(H, 5)} className="numeric">{res.investorPct != null ? fmtPct(res.investorPct) : "—"}</td>
              <td {...cell(H, 6)} className="numeric"><button className="btn btn-danger" disabled={ro} onClick={() => update((st) => { st.rounds = st.rounds.filter((x) => x.id !== r.id); })}>Remove</button></td>
            </tr>
          );
        })}
      </Table>
      {!state.rounds.length && <div id="roundsEmpty" className="empty-state">No priced rounds yet — most CPG brands raise a friends-and-family SAFE first, then a Seed priced round.</div>}
    </>
  );
}

function Kpi({ label, id, value, sub }) {
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="value" id={id} data-testid={id}>{value}</div>
      <div className="sub">{sub}</div>
    </div>
  );
}

const SUMMARY_HEADS = [["Holder", "30%"], ["Type", "18%"], ["Shares", "15%", 1], ["% of fully-diluted", "15%", 1], ["Ownership", "22%"]];
export function SummarySection({ computed }) {
  const H = SUMMARY_HEADS;
  const sorted = [...computed.holders].sort((a, b) => b.shares - a.shares).filter((h) => h.shares !== 0);
  return (
    <>
      <div className="kpi-grid">
        <Kpi label="Total shares outstanding" id="kpiShares" value={fmtInt(computed.totalShares)} sub="Founders + pool + investors" />
        <Kpi label="Post-money valuation" id="kpiPostMoney" value={computed.postMoney > 0 ? fmtMoney(computed.postMoney) : "—"} sub="After latest round" />
        <Kpi label="Total raised" id="kpiRaised" value={fmtMoney(computed.totalRaised)} sub="SAFEs + priced rounds" />
        <Kpi label="Founders combined" id="kpiFounders" value={fmtPct(computed.founderPctTotal)} sub="Fully-diluted %" />
      </div>
      <Table id="summaryTable" heads={H} style={{ marginTop: 24 }}>
          {sorted.map((h) => (
            <tr key={h.id} data-testid={`summary-row-${h.id}`}>
              <td {...cell(H, 0)}>{h.name}</td>
              <td {...cell(H, 1)}>{h.type}</td>
              <td {...cell(H, 2)} className="numeric">{fmtInt(h.shares)}</td>
              <td {...cell(H, 3)} className="numeric">{fmtPct(h.pct)}</td>
              <td {...cell(H, 4)}><div className="bar-cell"><div className="bar"><div className="bar-fill" style={{ width: `${Math.round(h.pct * 100)}%` }} /></div></div></td>
            </tr>
          ))}
      </Table>
    </>
  );
}
